import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useVenti } from 'venti'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { TableColumn } from 'react-data-table-component'
import {
	BranchUser,
	DraftContent,
	DraftLoanOfficerReassignRequest,
	LoanDraftSearchCriteria,
} from '@matech/thebigpos-sdk'
import { AxiosError } from 'axios'
import { useIntl } from 'react-intl'
import { ModalPleaseWait } from '@/components/modals/ModalPleaseWait'
import { TheBigPOSApi } from '@/lib/TheBigPOSClient'
import { useAlert } from '@/hooks'
import { formatDate } from '@/services/helper'
import { getTheme } from '@/config'
import { navigationLinking } from '@/services/navigation'
import { ApiError, LoanData } from '@/types'
import Switch from '@/components/formControls/Switch'
import queryKeys from '@/services/queryKeys'
import DataTable, { ActionItem } from '@/components/DataTable'
import useSearch from '@/hooks/useSearch'
import usePagination from '@/hooks/usePagination'
import useUser from '@/hooks/useUser'
import Badge from '@/components/Badge'
import ConfirmModal from '@/components/modals/ConfirmModal'

import {
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIRECTION,
} from '@/constants/pagination'
import CodePreviewModal from '@/components/code-preview-modal'
import AssignLoanOfficerModal from './AssignLoanOfficerModal'

import SearchFiltersLoanDraftsTab from './SearchFiltersLoanDraftsTab'
import { usePipelineShareContext } from '../providers/PipelineProvider'

const theme = getTheme()

const LoanDrafts = () => {
	const { alert } = useAlert()
	const ventiState = useVenti()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { isAdmin, isSuperAdmin } = useUser()
	const { formatMessage } = useIntl()
	const { handleOpenShareLoanModal } = usePipelineShareContext()

	const [reassignLOModalOpen, setReassignLOModalOpen] =
		useState(false)
	const [selectedRow, setSelectedRow] = useState<DraftContent | null>(
		null
	)
	const [filters, setFilters] = useState<LoanDraftSearchCriteria>({
		siteConfigurationId: 'all',
		loanOfficerId: 'all',
	})
	const [showDeleted, setShowDeleted] = useState(false)
	const { searchText, handleSearchChange, handleClearSearchClick } =
		useSearch()
	const [searchCriteria, setSearchCriteria] =
		useState<LoanDraftSearchCriteria>({
			searchText,
		})

	const [removeModalVisible, setRemoveModalVisible] = useState(false)
	const [fetching, setFetching] = useState(false)
	const [isSendToLos, setIsSendToLos] = useState(false)
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
	const [isViewModalOpen, setIsViewModalOpen] = useState(false)

	const {
		pageNumber,
		pageSize,
		sortBy,
		sortDirection,
		handleSort,
		handleRowsPerPageChange,
		handlePageChange,
	} = usePagination<DraftContent>({
		defaultSortBy: DEFAULT_SORT_BY,
		defaultSortDirection: DEFAULT_SORT_DIRECTION,
	})

	const {
		isError,
		isLoading,
		data: result,
		refetch,
	} = useQuery({
		queryKey: [
			queryKeys.loanDrafts,
			searchCriteria,
			pageNumber,
			pageSize,
			sortBy,
			sortDirection,
			showDeleted,
		],
		queryFn: () =>
			TheBigPOSApi.searchLoanDrafts(
				{
					...searchCriteria,
					includeDeleted: Boolean(isSuperAdmin && showDeleted),
				},
				{
					pageNumber,
					pageSize,
					sortBy,
					sortDirection,
				}
			),
	})

	useEffect(() => {
		if (reassignLOModalOpen) return
		setSelectedRow(null)
	}, [reassignLOModalOpen])

	useEffect(() => {
		setSearchCriteria((prev) => ({ ...prev, searchText }))
	}, [searchText])

	useEffect(() => {
		let formattedFilters = {
			...filters,
			isUnassigned: null,
		} as LoanDraftSearchCriteria
		if (filters.siteConfigurationId === 'all') {
			formattedFilters = {
				...formattedFilters,
				siteConfigurationId: null,
			}
		}
		if (filters.loanOfficerId !== formattedFilters.loanOfficerId) {
			formattedFilters = { ...formattedFilters, isUnassigned: false }
		}
		if (filters.loanOfficerId === 'unassigned') {
			formattedFilters = {
				...formattedFilters,
				isUnassigned: true,
				loanOfficerId: null,
			}
		}
		if (filters.loanOfficerId === 'all') {
			formattedFilters = { ...formattedFilters, loanOfficerId: null }
		}
		setSearchCriteria((prev) => ({ ...prev, ...formattedFilters }))
	}, [filters])

	useEffect(() => {
		if (!isError) return

		alert('There was a problem loading loan drafts', {
			severity: 'error',
		})
	}, [isError, alert])

	const columns: TableColumn<DraftContent>[] = [
		{
			name: formatMessage({ id: 'person.first_name' }),
			selector: (row: DraftContent) => row.user.firstName,
		},
		{
			name: formatMessage({ id: 'person.last_name' }),
			selector: (row: DraftContent) => row.user.lastName,
		},
		{
			name: formatMessage({ id: 'global.email' }),
			selector: (row: DraftContent) => row.user.email,
		},
		{
			name: formatMessage({ id: 'global.status' }),
			cell: (row: DraftContent) => {
				if (!row.deletedAt) return null
				const isLinked = Boolean(row.loanID)
				return (
					<Badge
						label={formatMessage({
							id: isLinked ? 'global.deleted_sent' : 'global.deleted',
						})}
						color="danger"
					/>
				)
			},
			ignoreRowClick: true,
			hide: 480,
			omit: !isSuperAdmin,
		},
		{
			name: formatMessage({ id: 'global.created_at' }),
			sortField: 'createdAt',
			selector: (row: DraftContent) => row.createdAt,
			cell: (row) => formatDate(row.createdAt, false),
			sortable: true,
		},
	]

	const adminColumns = [
		...columns,
		{
			name: formatMessage({ id: 'global.site' }),
			selector: (row: DraftContent) => row.siteConfiguration?.name,
			minWidth: '20%',
		},
		{
			name: formatMessage({ id: 'role.loanOfficer' }),
			selector: (row: DraftContent) =>
				row.loanOfficer
					? `${row.loanOfficer?.firstName} ${row.loanOfficer?.lastName}`
					: formatMessage({ id: 'loans.not_assigned' }),
		},
	]

	const getRouteName = (route: string) => {
		if (!route) return 'LOApply'

		const routes = [
			'Apply',
			'ApplyCoBorrower',
			'ApplySpanish',
			'Prequal',
			'PrequalCoBorrower',
		]
		return routes.includes(route) ? `LO${route}` : route
	}

	const showErrorMessage = (err: AxiosError<ApiError>) => {
		alert(
			err?.response?.data?.message || theme.api_messages.server_error,
			{
				severity: 'error',
			}
		)
	}

	const getLoanDraftMutation = useMutation({
		mutationFn: (id: string) => TheBigPOSApi.getLoanDraft(id),
		onSuccess: async (result) => {
			const draft = result?.data
			if (!draft) return
			if (draft.customData) {
				draft.customData = {
					targetHistory: [],
					routeName: getRouteName(draft.customData.routeName),
				}
			}
			ventiState.set(theme.storageKeys.loanDraft, draft)

			navigate(
				`/${navigationLinking[draft?.customData?.routeName as keyof typeof navigationLinking]}`
			)
		},
		onError: (err: AxiosError<ApiError>) => {
			showErrorMessage(err)
			setFetching(false)
		},
	})

	const handleRowCompleteClick = async (row: DraftContent) => {
		setFetching(true)
		await getLoanDraftMutation.mutateAsync(row.id)
	}

	const successMutation = async (message: string) => {
		alert(message, {
			severity: 'success',
		})
		await queryClient.invalidateQueries({
			queryKey: [queryKeys.loanDrafts],
		})
	}

	const deleteDraftMutation = useMutation({
		mutationFn: (id: string) => TheBigPOSApi.deleteLoanDraft(id),
		onSuccess: async () => {
			await successMutation(
				formatMessage(
					{ id: 'alerts.delete_success' },
					{ name: 'loan draft' }
				)
			)
		},
		onError: (err: AxiosError<ApiError>) => {
			setRemoveModalVisible(false)
			showErrorMessage(err)
		},
		onSettled: () => {
			setRemoveModalVisible(false)
			setSelectedRow(null)
			setRemoveModalVisible(false)
		},
	})

	const reassignLoanOfficerMutation = useMutation({
		mutationFn: (request: DraftLoanOfficerReassignRequest) =>
			TheBigPOSApi.reassignLoanOfficer(selectedRow!.id, request),
		onSuccess: async () => {
			await successMutation(
				formatMessage(
					{ id: 'alerts.save_success' },
					{ name: 'loan draft' }
				)
			)
		},
		onError: (err: AxiosError<ApiError>) => {
			showErrorMessage(err)
		},
		onSettled: () => {
			setRemoveModalVisible(false)
			setSelectedRow(null)
			setRemoveModalVisible(false)
		},
	})

	const handleRowViewClick = async (row: DraftContent) => {
		setSelectedRow(row)
		setIsViewModalOpen(true)
	}

	const handleViewClose = () => {
		setIsViewModalOpen(false)
		setSelectedRow(null)
	}

	const handleDeleteClick = async () => {
		if (!selectedRow) return
		await deleteDraftMutation.mutateAsync(selectedRow.id)
	}

	const handleRowDeleteClick = (row: DraftContent) => {
		setSelectedRow(row)
		setRemoveModalVisible(true)
	}

	const handleReassignSubmit = async (
		loanOfficer: BranchUser | null | undefined
	) => {
		if (!selectedRow) return
		const request = {
			loanOfficerID: loanOfficer?.id,
		} as DraftLoanOfficerReassignRequest
		await reassignLoanOfficerMutation.mutateAsync(request)
	}

	const handleRowReassignClick = async (row: DraftContent) => {
		setReassignLOModalOpen(true)
		setSelectedRow(row)
	}

	const handleRowSendToEncompassClick = async (row: DraftContent) => {
		setIsConfirmModalOpen(true)
		setSelectedRow(row)
	}

	const sendToEncompassMutation = useMutation({
		mutationFn: (request: LoanData) =>
			TheBigPOSApi.createLoan(request),
		onSuccess: async () => {
			await successMutation(
				formatMessage({ id: 'alerts.sent_to_los' })
			)
		},
		onError: (err: AxiosError<ApiError>) => {
			showErrorMessage(err)
		},
		onSettled: () => {
			setSelectedRow(null)
			setIsSendToLos(false)
			setIsConfirmModalOpen(false)
		},
	})

	const [restoreModalVisible, setRestoreModalVisible] =
		useState(false)

	const restoreDraftMutation = useMutation({
		mutationFn: async (id: string) => {
			return TheBigPOSApi.restoreLoanDraft(id)
		},
		onSuccess: async () => {
			await successMutation(
				formatMessage({ id: 'alerts.loan_draft_restored_success' })
			)
			setRestoreModalVisible(false)
			setSelectedRow(null)
		},
		onError: (err: AxiosError<ApiError>) => {
			showErrorMessage(err)
			setRestoreModalVisible(false)
		},
	})

	const handleRowRestoreClick = (row: DraftContent) => {
		setSelectedRow(row)
		setRestoreModalVisible(true)
	}

	const handleRestoreConfirm = async () => {
		if (!selectedRow) return
		await restoreDraftMutation.mutateAsync(selectedRow.id)
	}

	let actionItems: ActionItem<DraftContent>[] = [
		{
			name: formatMessage({ id: 'drafts.send_to_los' }),
			onClick: (
				_e: React.MouseEvent<Element, MouseEvent>,
				row: DraftContent
			) => handleRowSendToEncompassClick(row),
			hideIf: (row: DraftContent) => Boolean(row.deletedAt),
		},
		{
			name: formatMessage({ id: 'global.view' }),
			onClick: (
				_e: React.MouseEvent<Element, MouseEvent>,
				row: DraftContent
			) => handleRowViewClick(row),
		},
		{
			name: formatMessage({ id: 'global.delete' }),
			onClick: (
				_e: React.MouseEvent<Element, MouseEvent>,
				row: DraftContent
			) => handleRowDeleteClick(row),
			hideIf: (row: DraftContent) => Boolean(row.deletedAt),
		},
		{
			name: formatMessage({ id: 'global.share' }),
			onClick: (
				_e: React.MouseEvent<Element, MouseEvent>,
				row: DraftContent
			) =>
				handleOpenShareLoanModal?.({
					loanId: row.id,
					loanType: 'draft',
				}),
			hideIf: (row: DraftContent) => Boolean(row.deletedAt),
		},
	]

	if (isSuperAdmin) {
		actionItems = [
			{
				name: formatMessage({ id: 'remove_record.restore' }),
				onClick: (
					_e: React.MouseEvent<Element, MouseEvent>,
					row: DraftContent
				) => handleRowRestoreClick(row),
				hideIf: (row: DraftContent) => !row.deletedAt,
			},
			...actionItems,
		]
	}

	if (isAdmin) {
		actionItems = [
			{
				name: formatMessage({ id: 'loans.reassign_loan_officer' }),
				onClick: (
					_e: React.MouseEvent<Element, MouseEvent>,
					row: DraftContent
				) => handleRowReassignClick(row),
				hideIf: (row: DraftContent) => Boolean(row.deletedAt),
			},
			...actionItems,
		]
	} else {
		actionItems = [
			{
				name: formatMessage({ id: 'global.completed' }),
				onClick: (
					e: React.MouseEvent<Element, MouseEvent>,
					row: DraftContent
				) => handleRowCompleteClick(row),
				hideIf: (row: DraftContent) => Boolean(row.deletedAt),
			},
			...actionItems,
		]
	}

	const handleConfirm = async () => {
		setIsSendToLos(true)
		if (!selectedRow) return

		const result = await TheBigPOSApi.getLoanDraft(
			selectedRow?.id || ''
		)

		if (!result.data) return

		sendToEncompassMutation.mutateAsync({
			...result.data.applicationPayload,
			BorrowerEmail: selectedRow.user.email,
			BorrowerFirstName: selectedRow.user.firstName,
			BorrowerLastName: selectedRow.user.lastName,
			loanOfficerEmail: selectedRow.loanOfficer?.email,
			draftId: selectedRow.id,
			siteConfigurationId: selectedRow.siteConfiguration?.id,
		})
	}

	const handleCloseRemoveModal = () => {
		setRemoveModalVisible(false)
		setSelectedRow(null)
	}

	const handleCloseRestoreModal = () => {
		setRestoreModalVisible(false)
		setSelectedRow(null)
	}

	return (
		<>
			{selectedRow && (
				<CodePreviewModal
					value={selectedRow.applicationPayload}
					codeEditor={{
						language: 'json',
					}}
					open={isViewModalOpen}
					onClose={handleViewClose}
					title={formatMessage({ id: 'global.data' })}
					fullWidth
					maxWidth="md"
					scroll="body"
				/>
			)}

			<ConfirmModal
				open={removeModalVisible}
				onClose={handleCloseRemoveModal}
				onConfirm={handleDeleteClick}
				title={formatMessage({ id: 'remove_record.confirm_delete' })}
				subtitle={
					selectedRow
						? `${formatMessage({ id: 'remove_record.delete' })} "${selectedRow.user.firstName} ${selectedRow.user.lastName}" ${formatMessage({ id: 'global.draft' })}`
						: undefined
				}
				button={{
					loading: deleteDraftMutation.isPending,
					title: formatMessage({
						id: 'remove_record.confirm_deletion',
					}),
				}}
			/>
			<ConfirmModal
				open={restoreModalVisible}
				onClose={handleCloseRestoreModal}
				onConfirm={handleRestoreConfirm}
				title={formatMessage({
					id: 'remove_record.confirm_reactivate',
				})}
				subtitle={
					selectedRow
						? `${formatMessage({ id: 'remove_record.restore' })} "${selectedRow.user.firstName} ${selectedRow.user.lastName}" ${formatMessage({ id: 'global.draft' })}`
						: undefined
				}
				button={{
					loading: restoreDraftMutation.isPending,
					title: formatMessage({
						id: 'remove_record.confirm_restore',
					}),
				}}
			/>
			<AssignLoanOfficerModal
				open={reassignLOModalOpen}
				onClose={() => setReassignLOModalOpen(false)}
				onSubmit={handleReassignSubmit}
				item={selectedRow?.loanOfficer as BranchUser}
			/>
			<ModalPleaseWait
				visible={fetching}
				message="Fetching loan information"
			/>
			<ConfirmModal
				open={isConfirmModalOpen}
				button={{
					loading: isSendToLos || sendToEncompassMutation.isPending,
					title: formatMessage({
						id: 'global.yes',
					}),
				}}
				onConfirm={handleConfirm}
				onClose={() => setIsConfirmModalOpen(false)}
				title={formatMessage({ id: 'modals.confirm_send_to_los' })}
			/>
			<DataTable
				data={result?.data?.rows || []}
				columns={isAdmin ? adminColumns : columns}
				title={formatMessage({ id: 'pipeline.incomplete_loans_tab' })}
				progressPending={
					isLoading ||
					isSendToLos ||
					sendToEncompassMutation.isPending
				}
				onRefreshClick={refetch}
				fixedHeader
				actionItems={actionItems}
				onSearchChange={handleSearchChange}
				onSort={handleSort}
				searchText={searchText}
				onClearSearchClick={handleClearSearchClick}
				defaultSortFieldId={sortBy}
				defaultSortAsc={sortDirection === 'asc'}
				onChangePage={handlePageChange}
				onChangeRowsPerPage={handleRowsPerPageChange}
				pagination
				paginationTotalRows={result?.data?.count}
				paginationServer
				searchFilters={
					<>
						{isAdmin && (
							<SearchFiltersLoanDraftsTab setFilters={setFilters} />
						)}
						{isSuperAdmin && (
							<Switch
								className="mt-1"
								checked={showDeleted}
								label="Show Deleted Drafts"
								onChange={(_e, checked) => setShowDeleted(checked)}
								color="primary"
							/>
						)}
					</>
				}
			/>
		</>
	)
}

export default LoanDrafts
